import type { LocaleCode } from "@/pages/cogo-marketing/i18n/locales";
import type { DataCategory } from "@/store/types";

// ---------------------------------------------------------------------------
// Wave 5 — data surface localization.
//
// WHY THIS EXISTS: the 2026-08-18 slice-5A IDE-editor census
// (`docs/superpowers/specs/2026-08-18-slice5-ide-editors.md`, §3 "i18n")
// measured the data surface under the APPLIANCE profile
// (`VITE_COGO_DEFAULT_LOCALE=ko`) and read 34 body lines, **0 Korean**, 25
// English-only, calling it "the least-localized of the four surfaces". The
// reading was re-derived at `34dbcdc9a` with the census's own instrument and
// was unchanged: 34 / 0 / 25. Every label a Korean bank operator reads while
// wiring their app's data was English.
//
// The two consumers are `shell/ide-v2/surfaces/data-surface.tsx` (the
// navigator + toolbar) and `features/data-explorer/components/data-canvas.tsx`
// (the tabs, the readiness strip, export/import, the empty states, the status
// bar). This module is the single locale-keyed home for both, following the
// `COPY` table + `get…Copy(locale)` shape used by `admin-page-copy.ts` and
// `studio-publish-deploy-copy.ts`. It sits at the `features/data-explorer/`
// root because both consumers read it and, critically, both render the SAME
// four category labels — holding them in one table is what stops the two
// copies from drifting apart the way the census found elsewhere.
//
// `en` is byte-identical to the literals these files rendered before this
// change. `DEFAULT_LOCALE` is `en` and the existing `data-canvas.test.tsx`
// suite resolves controls by English accessible name
// (`getByRole('button', { name: 'Sources' })`), so an `en` drift fails that
// suite rather than regressing silently.
//
// TERMINOLOGY — checked against the Korean already in the product:
//  - `데이터소스` (datasource) matches the toast this very surface already
//    raises on create ("데이터소스 \"x\"을(를) 만들었습니다"), so the label and
//    the receipt for the same object read the same way.
//  - `변수` / `이벤트` / `블록` / `데이터` / `채팅 작업` are reused from the
//    existing Korean rail and dictionary tables.
//  - `액션플로우` (ActionFlow) and `플로우` (flow) follow the established
//    studio-dictionary rendering.
//
// KEPT VERBATIM — deliberately NOT in this table:
//  - **The `appData` / `local` / `block` state-scope pill labels.** These are
//    the canonical expression-prefix identifiers from the closed 9-prefix enum
//    (`appData.`, `local.`) that the operator literally types inside `#{…}`.
//    A translated scope name could not be matched back to the expression it
//    names. `data-canvas.tsx`'s `getStateScopeBucket()` returns these three as
//    values, and the `data-state-scope-<label>` testids are derived from them.
//  - Entity names, block names, endpoints, and `scopeDetail`'s numeric
//    breakdown — project payload, not copy.
// ---------------------------------------------------------------------------

export interface DataSurfaceCopy {
  /** The four data categories. Rendered by BOTH consumers — see header note. */
  categoryLabel: Record<DataCategory, string>;
  /** Readiness-strip metric labels; `flows` reads "ActionFlows", not "Flows". */
  metricLabel: Record<DataCategory, string>;
  /** Per-category create action, used by the empty state and the add button. */
  addAction: Record<DataCategory, string>;
  /** Per-category empty-state headline. */
  emptyTitle: Record<DataCategory, string>;

  /** Creator-persona Connect wrapper (essentials-connect-surface.tsx). */
  connectWorkspace: string;

  /** Toolbar + navigator (data-surface.tsx). */
  dataTitle: string;
  dataNavigation: string;
  reviewData: string;
  projectData: string;
  selectedEntity: string;
  selectEntityHint: string;
  chatActions: string;
  addSourceAction: string;
  connectAction: string;
  debugAction: string;

  /** Canvas chrome (data-canvas.tsx). */
  tableView: string;
  graphView: string;
  export: string;
  import: string;
  allData: string;
  filterPlaceholder: string;
  filterEntities: string;
  noMatchingEntities: string;
  stateModel: string;
  errorCount: (n: number) => string;
  warningCount: (n: number) => string;

  /** Status bar counts. */
  statusVariables: (n: number) => string;
  statusSources: (n: number) => string;
  statusEvents: (n: number) => string;
  statusFlows: (n: number) => string;
  statusBlocks: (n: number) => string;
  noProjectLoaded: string;
  unused: string;
}

const en: DataSurfaceCopy = {
  categoryLabel: {
    variables: "Variables",
    datasources: "Sources",
    events: "Events",
    flows: "Flows",
  },
  metricLabel: {
    variables: "Variables",
    datasources: "Sources",
    events: "Events",
    flows: "ActionFlows",
  },
  addAction: {
    variables: "Add variable",
    datasources: "Add source",
    events: "Add event",
    flows: "Add flow",
  },
  emptyTitle: {
    variables: "No variables declared",
    datasources: "No sources connected",
    events: "No events bound",
    flows: "No ActionFlows defined",
  },

  connectWorkspace: "Connect workspace",

  dataTitle: "Data",
  dataNavigation: "Data navigation",
  reviewData: "Review data",
  projectData: "Project data",
  selectedEntity: "Selected entity",
  selectEntityHint: "Select a row to inspect or change it with Coogo.",
  chatActions: "Chat actions",
  addSourceAction: "Add source",
  connectAction: "Connect",
  debugAction: "Debug",

  tableView: "Table view",
  graphView: "Graph view",
  export: "Export",
  import: "Import",
  allData: "All Data",
  filterPlaceholder: "Filter...",
  filterEntities: "Filter entities",
  noMatchingEntities: "No matching entities",
  stateModel: "Data & state model",
  errorCount: (n) => `${n} error${n !== 1 ? "s" : ""}`,
  warningCount: (n) => `${n} warning${n !== 1 ? "s" : ""}`,

  statusVariables: (n) => `${n} var${n !== 1 ? "s" : ""}`,
  statusSources: (n) => `${n} source${n !== 1 ? "s" : ""}`,
  statusEvents: (n) => `${n} event${n !== 1 ? "s" : ""}`,
  statusFlows: (n) => `${n} flow${n !== 1 ? "s" : ""}`,
  statusBlocks: (n) => `${n} block${n !== 1 ? "s" : ""}`,
  noProjectLoaded: "No project loaded",
  unused: "not used",
};

const ko: DataSurfaceCopy = {
  categoryLabel: {
    variables: "변수",
    datasources: "데이터소스",
    events: "이벤트",
    flows: "플로우",
  },
  metricLabel: {
    variables: "변수",
    datasources: "데이터소스",
    events: "이벤트",
    flows: "액션플로우",
  },
  addAction: {
    variables: "변수 추가",
    datasources: "데이터소스 추가",
    events: "이벤트 추가",
    flows: "플로우 추가",
  },
  emptyTitle: {
    variables: "선언된 변수가 없습니다",
    datasources: "연결된 데이터소스가 없습니다",
    events: "연결된 이벤트가 없습니다",
    flows: "정의된 액션플로우가 없습니다",
  },

  connectWorkspace: "연결 작업 공간",

  dataTitle: "데이터",
  dataNavigation: "데이터 탐색",
  reviewData: "데이터 검토",
  projectData: "프로젝트 데이터",
  selectedEntity: "선택한 항목",
  selectEntityHint: "행을 선택하면 자세히 보거나 Coogo로 변경할 수 있습니다.",
  chatActions: "채팅 작업",
  addSourceAction: "데이터소스 추가",
  connectAction: "연결",
  debugAction: "디버그",

  tableView: "표 보기",
  graphView: "그래프 보기",
  export: "내보내기",
  import: "가져오기",
  allData: "전체 데이터",
  filterPlaceholder: "필터...",
  filterEntities: "항목 필터",
  noMatchingEntities: "일치하는 항목이 없습니다",
  stateModel: "데이터 · 상태 모델",
  errorCount: (n) => `오류 ${n}건`,
  warningCount: (n) => `경고 ${n}건`,

  statusVariables: (n) => `변수 ${n}개`,
  statusSources: (n) => `데이터소스 ${n}개`,
  statusEvents: (n) => `이벤트 ${n}개`,
  statusFlows: (n) => `플로우 ${n}개`,
  statusBlocks: (n) => `블록 ${n}개`,
  noProjectLoaded: "프로젝트가 없습니다",
  unused: "사용 안 함",
};

const ja: DataSurfaceCopy = {
  categoryLabel: {
    variables: "変数",
    datasources: "データソース",
    events: "イベント",
    flows: "フロー",
  },
  metricLabel: {
    variables: "変数",
    datasources: "データソース",
    events: "イベント",
    flows: "アクションフロー",
  },
  addAction: {
    variables: "変数を追加",
    datasources: "データソースを追加",
    events: "イベントを追加",
    flows: "フローを追加",
  },
  emptyTitle: {
    variables: "変数が宣言されていません",
    datasources: "データソースが接続されていません",
    events: "イベントが紐づけられていません",
    flows: "アクションフローが定義されていません",
  },

  connectWorkspace: "接続ワークスペース",

  dataTitle: "データ",
  dataNavigation: "データナビゲーション",
  reviewData: "データを確認",
  projectData: "プロジェクトデータ",
  selectedEntity: "選択中の項目",
  selectEntityHint: "行を選択すると内容を確認したり Coogo で変更できます。",
  chatActions: "チャット操作",
  addSourceAction: "データソースを追加",
  connectAction: "接続",
  debugAction: "デバッグ",

  tableView: "テーブル表示",
  graphView: "グラフ表示",
  export: "エクスポート",
  import: "インポート",
  allData: "すべてのデータ",
  filterPlaceholder: "絞り込み...",
  filterEntities: "項目を絞り込む",
  noMatchingEntities: "一致する項目がありません",
  stateModel: "データ・状態モデル",
  errorCount: (n) => `エラー ${n} 件`,
  warningCount: (n) => `警告 ${n} 件`,

  statusVariables: (n) => `変数 ${n} 個`,
  statusSources: (n) => `データソース ${n} 個`,
  statusEvents: (n) => `イベント ${n} 個`,
  statusFlows: (n) => `フロー ${n} 個`,
  statusBlocks: (n) => `ブロック ${n} 個`,
  noProjectLoaded: "プロジェクトがありません",
  unused: "未使用",
};

const zh: DataSurfaceCopy = {
  categoryLabel: {
    variables: "变量",
    datasources: "数据源",
    events: "事件",
    flows: "流程",
  },
  metricLabel: {
    variables: "变量",
    datasources: "数据源",
    events: "事件",
    flows: "动作流程",
  },
  addAction: {
    variables: "添加变量",
    datasources: "添加数据源",
    events: "添加事件",
    flows: "添加流程",
  },
  emptyTitle: {
    variables: "尚未声明变量",
    datasources: "尚未连接数据源",
    events: "尚未绑定事件",
    flows: "尚未定义动作流程",
  },

  connectWorkspace: "连接工作区",

  dataTitle: "数据",
  dataNavigation: "数据导航",
  reviewData: "检查数据",
  projectData: "项目数据",
  selectedEntity: "已选项目",
  selectEntityHint: "选择一行即可查看详情，或用 Coogo 修改。",
  chatActions: "聊天操作",
  addSourceAction: "添加数据源",
  connectAction: "连接",
  debugAction: "调试",

  tableView: "表格视图",
  graphView: "关系图视图",
  export: "导出",
  import: "导入",
  allData: "全部数据",
  filterPlaceholder: "筛选...",
  filterEntities: "筛选条目",
  noMatchingEntities: "没有匹配的条目",
  stateModel: "数据与状态模型",
  errorCount: (n) => `${n} 个错误`,
  warningCount: (n) => `${n} 个警告`,

  statusVariables: (n) => `${n} 个变量`,
  statusSources: (n) => `${n} 个数据源`,
  statusEvents: (n) => `${n} 个事件`,
  statusFlows: (n) => `${n} 个流程`,
  statusBlocks: (n) => `${n} 个区块`,
  noProjectLoaded: "没有项目",
  unused: "未使用",
};

const zhTW: DataSurfaceCopy = {
  categoryLabel: {
    variables: "變數",
    datasources: "資料來源",
    events: "事件",
    flows: "流程",
  },
  metricLabel: {
    variables: "變數",
    datasources: "資料來源",
    events: "事件",
    flows: "動作流程",
  },
  addAction: {
    variables: "新增變數",
    datasources: "新增資料來源",
    events: "新增事件",
    flows: "新增流程",
  },
  emptyTitle: {
    variables: "尚未宣告變數",
    datasources: "尚未連接資料來源",
    events: "尚未繫結事件",
    flows: "尚未定義動作流程",
  },

  connectWorkspace: "連接工作區",

  dataTitle: "資料",
  dataNavigation: "資料導覽",
  reviewData: "檢查資料",
  projectData: "專案資料",
  selectedEntity: "已選取項目",
  selectEntityHint: "選取一列即可查看詳細內容，或用 Coogo 修改。",
  chatActions: "聊天操作",
  addSourceAction: "新增資料來源",
  connectAction: "連接",
  debugAction: "偵錯",

  tableView: "表格檢視",
  graphView: "關聯圖檢視",
  export: "匯出",
  import: "匯入",
  allData: "全部資料",
  filterPlaceholder: "篩選...",
  filterEntities: "篩選項目",
  noMatchingEntities: "沒有符合的項目",
  stateModel: "資料與狀態模型",
  errorCount: (n) => `${n} 個錯誤`,
  warningCount: (n) => `${n} 個警告`,

  statusVariables: (n) => `${n} 個變數`,
  statusSources: (n) => `${n} 個資料來源`,
  statusEvents: (n) => `${n} 個事件`,
  statusFlows: (n) => `${n} 個流程`,
  statusBlocks: (n) => `${n} 個區塊`,
  noProjectLoaded: "沒有專案",
  unused: "未使用",
};

const COPY: Record<LocaleCode, DataSurfaceCopy> = {
  en,
  ko,
  ja,
  zh,
  "zh-TW": zhTW,
};

export function getDataSurfaceCopy(locale: LocaleCode): DataSurfaceCopy {
  return COPY[locale];
}
